<!--
	@component
	Interactive chip list with a text input to add entries and per-chip remove buttons.
	Supports read-only mode, per-entry read-only/failed states, single-line overflow with
	a "+N more" tooltip, and form-error display via the `formErrors` context.
-->
<script lang="ts">
	import { getContext, hasContext, onMount, tick } from 'svelte'
	import MaterialInputField from '../molecules/MaterialInputField.svelte'
	import {
		DefaultInputFieldProps,
		DefaultRobotoFontProps,
		type FontProps,
		getFontPropsVarString
	} from '$types/fontProps'
	import { t } from '$i18n/i18n'
	import Tooltip from '../atoms/Tooltip.svelte'
	import { inputFieldFormats } from '$utils/formatters'
	import iconAddAutocomplete from '@/icons/icon_add_autocomplete_entry.svg'
	import { TooltipPlacements } from '$types/enums'
	import { slide } from 'svelte/transition'
	import type { TransitionConfig } from 'svelte/transition'
	import type { Writable } from 'svelte/store'
	import type { FormError } from '$types/formError'
	import {
		getExactPathErrorMessage,
		getPathErrorMessage,
		isExactPathInErrorList,
		isPathInErrorList
	} from '$utils/formValidation'
	import Tooltip_v5 from '#lib/atoms/Tooltip_v5.svelte'
	import AutocompleteInputField from '#lib/autocomplete/AutocompleteInputField.svelte'
	import type { AutocompleteFieldConfig } from '$types/autocompleteFieldConfig'
	import Button from '#lib/atoms/Button.svelte'
	import IconPlus from '$svg/icon-plus.svg?component'

	type ChipListProps = {
		showInput?: boolean
		placeholder?: string
		list?: string[]
		readOnlyEntries?: string[] | null
		failedEntries?: string[] | null
		readOnly?: boolean
		onInputFinished?: () => void
		materialWidth?: string
		materialLabel?: string
		borderless?: boolean
		fontProps?: FontProps
		format?: typeof inputFieldFormats.FULL
		singleLineMode?: boolean
		moreChipAddon?: string | undefined
		maxTooltipWidth?: number
		chipFontProps?: FontProps
		chipPadding?: string
		chipRadius?: string | null
		errorPath?: string
		autocompleteConfig?: AutocompleteFieldConfig
		options?: object[]
		onfocusout?: (e: FocusEvent) => void
		onChipRemoved?: (label: string) => void
		onAddChip?: (label: string) => void
	}

	function errorReveal(node: Element, { duration = 200 } = {}): TransitionConfig {
		const { css: slideCss } = slide(node, { duration })
		return {
			duration,
			css: (t: number, u: number) => `${slideCss?.(t, u) ?? ''} opacity: ${t};`
		}
	}

	const useMaterialDesign: boolean = hasContext('useMaterialDesign')
		? getContext('useMaterialDesign')
		: false

	let {
		// Toggle the visibility of the input field.
		showInput = true,
		// Placeholder for the input field. If we have a material Label, we can show both the materialLabel AND the placeholder.
		placeholder = undefined,
		// The list of chips that is included in total in the component.
		list = $bindable<string[]>([]),
		// The list of entries, that are read only and can not be deleted. If not included in the list, it is ignored.
		readOnlyEntries = null,
		// The list of entries, that are in a failed state. If not included in the list, it is ignored.
		failedEntries = null,
		// Make the complete component read only.
		readOnly = false,
		onInputFinished = () => {},
		materialWidth = '100%',
		// The label for the material design input field. If activated, it can be used (with placeholder) for material design.
		materialLabel = '',
		// Hides the borders of the input field. Default set to false.
		borderless = false,
		fontProps = DefaultInputFieldProps(),
		format = inputFieldFormats.FULL,
		singleLineMode = false,
		moreChipAddon = undefined,
		// The maximum width of the tooltip in px.
		maxTooltipWidth = 400,
		chipFontProps = DefaultRobotoFontProps('0.75rem', '0.625rem'),
		chipPadding = '0.25rem 0.5rem 0.2rem 0.5rem',
		chipRadius = null,
		errorPath = '',
		autocompleteConfig = undefined,
		// Forwarded verbatim to the autocomplete field; svelecte expects option objects.
		options = undefined,
		onfocusout = undefined,
		onChipRemoved = undefined,
		onAddChip = undefined
	}: ChipListProps = $props()

	let value = $state('')

	let containerRef: HTMLElement | undefined = $state()
	let chipRefs: (HTMLElement | undefined)[] = $state([])
	let measurementMoreChip: HTMLElement | undefined = $state()
	let visibleChips: string[] = $state([])
	let hiddenChips: string[] = $state([])

	const pad = $derived(
		chipPadding && chipPadding !== '' ? '--chip-padding: ' + chipPadding + ';' : ''
	)
	const radius = $derived(
		chipRadius && chipRadius !== '' ? '--chip-radius: ' + chipRadius + ';' : ''
	)

	/* Form error handling via context store */
	const formErrors: Writable<FormError[]> | null = hasContext('formErrors')
		? getContext('formErrors')
		: null

	const error = $derived.by(() => {
		if (!errorPath || !formErrors) return ''
		const errors = $formErrors
		if (errors === null) return ''
		let result = ''
		const paths = errorPath.includes('||') ? errorPath.split('||') : [errorPath]
		paths.forEach((path) => {
			if (path.includes('.*')) {
				result += isPathInErrorList(errors, path) ? getPathErrorMessage(errors, path) : ''
			} else {
				result += isExactPathInErrorList(errors, path) ? getExactPathErrorMessage(errors, path) : ''
			}
		})
		return result
	})

	onMount(() => {
		if (!containerRef) return

		const resizeObserver = new ResizeObserver(() => {
			calculateVisibleChips()
		})

		resizeObserver.observe(containerRef)

		return () => {
			resizeObserver.disconnect()
		}
	})

	$effect(() => {
		// Re-run whenever list or singleLineMode changes (e.g. tags loaded async by the parent).
		// Reads list synchronously so Svelte tracks it as a dependency.
		// Safe: this effect only reads list/singleLineMode and writes visibleChips/hiddenChips —
		// it never reads what it writes, so it cannot loop.
		void list
		void singleLineMode
		calculateVisibleChips()
	})

	function addValue() {
		const strippedValue = value?.trim()

		// Do nothing when value is empty.
		if (!strippedValue) return

		onInputFinished()
		if (strippedValue && !list.includes(strippedValue)) {
			list = [...list, strippedValue]
		}
		value = ''
		onAddChip?.(strippedValue)
	}

	function removeItem(itemToRemove: string) {
		if (list.includes(itemToRemove)) {
			list = list.filter((item) => item !== itemToRemove)
			visibleChips = visibleChips.filter((item) => item !== itemToRemove)
			hiddenChips = hiddenChips.filter((item) => item !== itemToRemove)
			onChipRemoved?.(itemToRemove)
		}
	}

	function handleKeyDown(e: KeyboardEvent) {
		if (e.key === 'Enter') {
			addValue()
		}
	}

	async function calculateVisibleChips() {
		if (!singleLineMode) {
			visibleChips = [...list]
			hiddenChips = []
			return
		}

		if (!containerRef || !measurementMoreChip || list.length === 0) return

		await tick()

		// The component may have unmounted during the await (e.g. ResizeObserver firing
		// as a story tears down); the bound refs are null again, so re-check before measuring.
		if (!containerRef || !measurementMoreChip) return

		const containerWidth = containerRef.offsetWidth
		let currentWidth = 0
		let visibleCount = 0
		const moreChipWidth = measurementMoreChip.offsetWidth + 8

		for (let i = 0; i < chipRefs.length; i++) {
			if (!chipRefs[i]) continue

			const chipWidth = chipRefs[i]!.offsetWidth + 8
			const wouldNeedMoreChip = i < list.length - 1
			const requiredWidth = currentWidth + chipWidth + (wouldNeedMoreChip ? moreChipWidth : 0)

			if (requiredWidth <= containerWidth) {
				currentWidth += chipWidth
				visibleCount++
			} else {
				break
			}
		}

		/**
		 * the loop already reserves moreChipWidth — adjusting visibleCount here
		 * double-counts it and can go negative, where slice(0, -1) keeps all but the last
		 */
		visibleChips = list.slice(0, visibleCount)
		hiddenChips = list.slice(visibleCount)
	}

	function isReadOnly(item: string) {
		return (readOnlyEntries && readOnlyEntries.includes(item)) || readOnly
	}

	function isFailed(item: string) {
		return failedEntries && failedEntries.includes(item)
	}

	function getClassList(item: string) {
		let res = ''
		if (isReadOnly(item)) res += 'read-only '
		if (isFailed(item)) res += 'failed '
		return res
	}
</script>

<div bind:this={containerRef} class="chip-list-container">
	{#if showInput}
		{#if autocompleteConfig}
			<div class="chip-list__autocomplete-container">
				<AutocompleteInputField bind:value {options} {autocompleteConfig} label={placeholder} />
				{#if !readOnly}
					<Tooltip_v5 msg={$t('UI.chipList.addEntry')} zIndex={1000003}>
						<Button variant="primary" disabled={!value} width="32px" onclick={addValue}>
							<IconPlus width="20px" height="20px" />
						</Button>
					</Tooltip_v5>
				{/if}
			</div>
		{:else}
			<div class="input-with-plus">
				{#if useMaterialDesign}
					<MaterialInputField
						bind:value
						{placeholder}
						{onInputFinished}
						{fontProps}
						{borderless}
						width={materialWidth}
						disabled={readOnly}
						label={materialLabel}
						on:keydown={handleKeyDown}
					/>
				{:else}
					<input
						bind:value
						class="input-field-focus"
						type="text"
						class:borderless
						style="height: 2rem; width: {format.maxWidth}; max-width: {format.maxWidth}"
						disabled={readOnly}
						{placeholder}
						{onfocusout}
						onblur={onInputFinished}
						onkeydown={handleKeyDown}
					/>
				{/if}
				{#if !readOnly}
					<button type="button" class="add-chip-button" onclick={addValue}>
						<div class="green-icon">
							<Tooltip
								msg={$t('UI.chipList.addEntry')}
								zIndex={1000003}
								ttipIconImage={iconAddAutocomplete}
								ttipIconWidth="20px"
								ttipIconHeight="20px"
							/>
						</div>
					</button>
				{/if}
			</div>
		{/if}
	{/if}
	{#snippet removeChipButton(item: string)}
		<button
			type="button"
			class="remove-chip-btn"
			aria-label={$t('UI.chipList.removeEntry', { entry: item })}
			onclick={() => removeItem(item)}
		>
			<div></div>
		</button>
	{/snippet}
	<div class="chip-list" style={getFontPropsVarString(chipFontProps, 'chip')}>
		{#each visibleChips.filter((i) => i !== '') as item, i (`${i}-${item}`)}
			<div class="chip {getClassList(item)}" style="{pad}{radius}">
				<span>{item}</span>
				{#if !isReadOnly(item)}
					{@render removeChipButton(item)}
				{/if}
			</div>
		{/each}
		{#if singleLineMode && hiddenChips.length > 0}
			<Tooltip_v5
				maxWidthTtip={maxTooltipWidth}
				placementTtip={TooltipPlacements.BOTTOM_START}
				offsetTtip={[10, 10]}
				zIndex={1000003}
			>
				<div class="chip more-chip-wrapper {readOnly ? 'read-only' : ''}" style="{pad}{radius}">
					<span
						>+{hiddenChips.length}
						{$t('UI.chipList.more')}{moreChipAddon ? ` ${moreChipAddon}` : ''}</span
					>
				</div>
				{#snippet content()}
					<div class="chip-list">
						{#each hiddenChips.filter((i) => i !== '') as item, i (`${i}-${item}`)}
							<div class="chip {getClassList(item)}" style="{pad}{radius}">
								<span>{item}</span>
								{#if !isReadOnly(item)}
									{@render removeChipButton(item)}
								{/if}
							</div>
						{/each}
					</div>
				{/snippet}
			</Tooltip_v5>
		{/if}
	</div>
	<!-- hidden refChips to measure the size; aria-hidden so labels are not announced twice -->
	<div class="measurement-container" aria-hidden="true">
		<!-- keys are index-composite because consumer lists may contain duplicate strings -->
		{#each list as chip, index (`${index}-${chip}`)}
			{#if (readOnlyEntries && readOnlyEntries.includes(chip)) || readOnly}
				<div
					class="chip read-only measurement-chip"
					style="{pad}{radius}"
					bind:this={chipRefs[index]}
				>
					<span>{chip}</span>
				</div>
			{:else}
				<div class="chip measurement-chip" style="{pad}{radius}" bind:this={chipRefs[index]}>
					<span>{chip}</span>
					<div class="remove-chip-btn">
						<div></div>
					</div>
				</div>
			{/if}
		{/each}
		<!-- list.length, not hiddenChips.length: calculateVisibleChips measures this to
		     reserve room, so a label from its own result reads "+0" on the first pass -->
		<div bind:this={measurementMoreChip} class="chip more-chip-wrapper" style="{pad}{radius}">
			<span
				>+{list.length}
				{$t('UI.chipList.more')}{moreChipAddon ? ` ${moreChipAddon}` : ''}</span
			>
		</div>
	</div>
	{#if error && error !== ''}
		<span class="form-field-error" transition:errorReveal>{@html error}</span>
	{/if}
</div>

<style lang="scss">
	@import '$styles/global';

	.chip-list-container {
		@include flex-col(0.375rem, $alignment: flex-start);

		width: 100%;

		& > .input-with-plus {
			width: 100%;
			position: relative;
			height: 2rem;

			& > .add-chip-button {
				position: absolute;
				top: 4px;
				right: 0;
			}
		}
	}

	.chip-list {
		@include flex-row(0.5rem, $justify: flex-start);

		flex-wrap: wrap;
		row-gap: 0.375rem;
	}

	.remove-chip-btn {
		@include flex-col;

		width: 0.625rem;
		height: 0.625rem;
		cursor: pointer;

		& > div {
			width: 0.5rem;
			height: 0.5rem;
			background: url('@/icons/icon_delete_chip.svg') no-repeat center;
		}
	}

	.chip {
		border: 1px solid $primaryGreen;

		@include roboto-font(
			var(--chip-line-height),
			var(--chip-font-weight),
			var(--chip-font-size),
			var(--chip-color)
		);
		@include flex-row(0.25rem, $alignment: center);

		border-radius: var(--chip-border-radius, 2.125rem);
		padding: var(--chip-padding);

		& > span {
			line-height: normal;
		}

		&.read-only {
			border: 1px solid var(--color-gray-400);
			cursor: not-allowed;

			& span {
				color: #616161;
			}
		}

		&.failed {
			border: 1px solid $errorRed;
		}
	}

	button.add-chip-button,
	button.remove-chip-btn {
		background: none;
		border: none;
		padding: 0;
		margin: 0;
	}

	.green-icon {
		background-color: $primaryGreen;
		border-radius: 4px;
		margin-right: 0.375rem;
		width: 24px;
		height: 24px;

		@include flex-col;
	}

	.measurement-container {
		position: absolute;
		top: -9999px;
		left: -9999px;
		visibility: hidden;
		display: flex;
		gap: 0.375rem;
	}

	.measurement-chip {
		visibility: hidden;
	}

	.chip-list__autocomplete-container {
		display: flex;
		width: 100%;
		gap: 4px;
		align-items: flex-end;
	}
</style>
